import React from 'react';
import { useTranslation } from 'react-i18next';
import { Course, CourseProgress } from '../../../types';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Share2, Bookmark } from 'lucide-react';
import { getMediaUrl } from '../../../utils/media';

interface CourseHeaderProps {
    course: Course;
    progress?: CourseProgress | null;
    onEnroll?: () => void;
    onContinue?: () => void;
}

export const CourseHeader: React.FC<CourseHeaderProps> = ({
    course,
    progress,
    onEnroll,
    onContinue
}) => {
    const { t } = useTranslation();
    const isEnrolled = !!progress;
    const percent = progress?.progress || 0;
    const isCompleted = percent >= 100;
    const defaultThumbnail = 'linear-gradient(135deg,rgb(241, 65, 65) 0%,rgb(89, 240, 215) 100%)';

    return (
        <div className="relative mb-8 rounded-3xl overflow-hidden bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 border border-white/50 shadow-sm">
            <div className="absolute inset-0 bg-grid-black/[0.02] -z-10" />

            <div className="flex flex-col md:flex-row gap-6 p-6 md:p-10">
                {/* Thumbnail */}
                <div
                    className="w-full md:w-1/3 aspect-video rounded-2xl shadow-lg bg-cover bg-center shrink-0"
                    style={{
                        backgroundImage: course.thumbnail
                            ? `url(${getMediaUrl(course.thumbnail)})`
                            : defaultThumbnail
                    }}
                />

                {/* Content */}
                <div className="flex-1 flex flex-col justify-center space-y-4">
                    <div className="flex flex-wrap gap-2">
                        <Badge variant="secondary" className="bg-white/80 backdrop-blur text-indigo-600 border-indigo-100">
                            {course.category?.name || t('course.header.category', { defaultValue: 'General' })}
                        </Badge>
                        <Badge variant="outline" className={`
              ${course.level === 'beginner' ? 'text-green-600 border-green-200 bg-green-50' : ''}
              ${course.level === 'intermediate' ? 'text-blue-600 border-blue-200 bg-blue-50' : ''}
              ${course.level === 'advanced' ? 'text-purple-600 border-purple-200 bg-purple-50' : ''}
            `}>
                            {t(`classes.filters.${course.level}`, { defaultValue: course.level })}
                        </Badge>
                    </div>

                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                        {course.title}
                    </h1>

                    <p className="text-gray-600 text-lg line-clamp-2">
                        {course.description || t('classes.course.updatingDescription')}
                    </p>

                    <div className="flex flex-wrap items-center gap-4 pt-2">
                        {isEnrolled ? (
                            <Button
                                size="lg"
                                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-indigo-200 shadow-lg"
                                onClick={onContinue}
                            >
                                {isCompleted ? t('course.header.completed') : t('course.header.continue')}
                            </Button>
                        ) : (
                            <Button
                                size="lg"
                                className="bg-gray-900 hover:bg-gray-800 text-white rounded-xl shadow-lg"
                                onClick={onEnroll}
                            >
                                {t('course.header.enrollNow')}
                            </Button>
                        )}

                        <div className="flex gap-2">
                            <Button variant="outline" size="icon" className="rounded-xl" title={t('course.header.share')}>
                                <Share2 className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="icon" className="rounded-xl" title={t('course.header.save')}>
                                <Bookmark className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
